import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';

type QueryFn<TData> = () => Promise<TData>;

export function createQueryHook<TData, TError = Error, TKey extends QueryKey = QueryKey>(
  key: TKey,
  queryFn: QueryFn<TData>,
  defaultOptions?: Omit<UseQueryOptions<TData, TError, TData, TKey>, 'queryKey' | 'queryFn'>
) {
  return (
    options?: Omit<UseQueryOptions<TData, TError, TData, TKey>, 'queryKey' | 'queryFn'>
  ): UseQueryResult<TData, TError> => {
    return useQuery<TData, TError, TData, TKey>({
      queryKey: key,
      queryFn,
      ...defaultOptions,
      ...options,
    });
  };
}

export function createMutationHook<TVariables, TData, TError = Error>(
  mutationFn: (vars: TVariables) => Promise<TData>
) {
  return (
    options?: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>
  ): UseMutationResult<TData, TError, TVariables> => {
    return useMutation<TData, TError, TVariables>({
      mutationFn,
      ...options,
    });
  };
}

export function createQuery<TData, TParams, TKey extends readonly unknown[]>({
  key,
  queryFn,
  enabled,
  options,
}: {
  key: (params: TParams) => TKey;
  queryFn: (params: TParams) => Promise<TData>;
  enabled?: (params: TParams) => boolean;
  options?: Omit<UseQueryOptions<TData, Error, TData, TKey>, 'queryKey' | 'queryFn' | 'enabled'>;
}) {
  return (params: TParams) => {
    const queryHook = createQueryHook<TData, Error, TKey>(key(params), () => queryFn(params));

    return queryHook({
      enabled: enabled ? enabled(params) : true,
      ...options,
    });
  };
}
